import React, { useEffect } from 'react';
import { reduxForm, Field } from 'redux-form/immutable';
import type { FormSubmitHandler, InjectedFormProps } from 'redux-form';
import { useHistory, useRouteMatch } from 'react-router';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import { LinearProgress, Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { fromJS, List } from 'immutable';
import { captureException } from '@sentry/react';

import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import * as validators from 'utils/validators';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import FormFieldLabel from 'retired/FormFieldLabel';
import { selectUser, selectOrganization, selectUserLocations } from 'entities/user/selectors';
import FormSelectBoxEntitySelector from 'retired/FormSelectBoxEntitySelector';
import PhoneNumber from 'retired/PhoneNumberField/index';
import OrganizationApi from 'entities/organization/api';
import { Privileges } from 'utils/privileges';
import locales from 'utils/locales';

import { FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';
import { initAction, saveAction } from './actions';
import type { FormData } from './actions';

const key = 'VolunteerFormPage';

const required = validators.required();
const notEmpty = validators.notEmpty();
const isEmail = validators.isEmail();
const isPhone = validators.isPhone();

const VolunteerFormPage = ({
  invalid,
  pristine,
  submitting,
  handleSubmit,
}: InjectedFormProps<FormData>): JSX.Element => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const dispatch = useDispatch();
  const match = useRouteMatch<{ id: string }>('/volunteers/:id/edit');
  const history = useHistory();
  const loading = useSelector(selectLoading);
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const locations = useSelector(selectUserLocations);

  useEffect(() => {
    if (
      user &&
      (!organization?.isCompany() || !user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS))
    ) {
      history.replace('/');
    }
    if (match) {
      dispatch(initAction(match.params.id));
    } else {
      captureException(
        new Error("VolunteerFormPage should always match the route '/volunteers/:id/edit'"),
        { fingerprint: ['VolunteerFormPage', 'No match', '/volunteers/:id/edit'] }
      );
    }
  }, []);
  if (loading) {
    return <LinearProgress />;
  }

  const submit: FormSubmitHandler<FormData> = (data: FormData) => {
    if (match) {
      dispatch(
        saveAction({
          id: match.params.id,
          // redux-form does not type FormData as immutable correctly
          ...data.toJS(),
        })
      );
    }
  };

  return (
    <Box>
      <Helmet title="Edit Employee" />
      <div>
        <form onSubmit={handleSubmit(submit)}>
          <fieldset>
            <Grid
              container
              justifyContent="space-between"
              direction="row"
              alignItems="center"
              spacing={3}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={4}>
                <Field
                  id="firstName"
                  name="firstName"
                  component={FormTextField}
                  fullWidth
                  label="First Name *"
                  validate={[required, notEmpty]}
                />
              </Grid>
              <Grid item xs={4}>
                <Field
                  id="lastName"
                  name="lastName"
                  component={FormTextField}
                  fullWidth
                  autoFocus
                  label="Last Name *"
                  validate={[required, notEmpty]}
                />
              </Grid>
              <Grid item xs={4}>
                <Field
                  id="email"
                  name="email"
                  component={FormTextField}
                  fullWidth
                  autoFocus
                  label="Email *"
                  validate={[required, notEmpty, isEmail]}
                />
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="space-between"
              direction="row"
              alignItems="center"
              spacing={3}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={4}>
                <Field
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  component={PhoneNumber}
                  country={organization?.locationObject.countryCode}
                  fullWidth
                  autoFocus
                  label="Phone Number"
                  validate={isPhone}
                />
              </Grid>

              <Grid item xs={4}>
                <FormFieldLabel>Location *</FormFieldLabel>
                <Field
                  id="location"
                  name="location"
                  component={FormSelectBoxEntitySelector}
                  options={locations}
                  fullWidth
                  autoFocus
                  label="Location *"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                  helperText="The user will be allowed to view deeds from the selected area"
                  validate={[required, notEmpty]}
                  showSearch
                />
              </Grid>

              <Grid item xs={4}>
                <FormFieldLabel>Department</FormFieldLabel>
                <Field
                  id="department"
                  name="department"
                  fullWidth
                  autoFocus
                  component={FormSelectBoxEntitySelector}
                  options={
                    organization?.get('departments').length
                      ? List(organization?.get('departments'))
                      : undefined
                  }
                  fetchOptions={OrganizationApi.fetchDepartments}
                  placeholder="Select departments"
                  label="Department"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                  showSearch
                />
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="flex-start"
              direction="row"
              alignItems="center"
              spacing={3}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={4}>
                <FormFieldLabel>T-Shirt Size</FormFieldLabel>
                <Field
                  id="tShirtSize"
                  name="tShirtSize"
                  component={FormSelectBoxEntitySelector}
                  options={List(['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'])}
                  fullWidth
                  autoFocus
                  label="T-Shirt Size"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormFieldLabel>Locale</FormFieldLabel>
                <Field
                  id="locale"
                  name="locale"
                  component={FormSelectBoxEntitySelector}
                  options={fromJS(locales)}
                  fullWidth
                  autoFocus
                  label="Locale"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                />
              </Grid>
            </Grid>

            <div
              style={{
                display: 'flex',
                justifyContent: 'left',
                alignItems: 'left',
              }}
            >
              <Button
                type="submit"
                style={{ marginRight: '3rem' }}
                variant="contained"
                color="primary"
                disabled={invalid || submitting || pristine}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => match && history.push(`/volunteers/${match.params.id}`)}
              >
                Cancel
              </Button>
            </div>
          </fieldset>
        </form>
      </div>
    </Box>
  );
};

const withForm = reduxForm<FormData>({
  form: FORM_IDENTIFIER,
});

export default compose(withForm, PureRender)(VolunteerFormPage);
